// ================================================================
// apiClient — die EINE Quelle für authentifizierte Fetch-Aufrufe + Fehlerbehandlung.
// ================================================================
// Ersetzt 16 fast identische Kopien derselben ~15 Zeilen (auth.js, customers.js,
// documents.js, email.js, oauth.js, org.js, platform.js, projects.js, runner.js,
// scenarios.js, timeEntries.js, whatsapp.js, prompts-store.js, credentials-store.js,
// SettingsModal.js) — jede mit eigenen kleinen Abweichungen, wie ein Fehler aus dem
// Response-Body gelesen wird.
//
// Der eigentliche Grund für dieses Modul: das Backend liefert Fehler jetzt einheitlich
// als { error: { code, message, details? } } (docs/i18n.md, Regel 4 — "Server liefert
// Key/Code, Frontend übersetzt"). Übersetzt wird HIER, EINMAL: err.message ist ab jetzt
// der lokalisierte Text (t('errors.<code>'), wenn der Code eine Übersetzung hat — sonst
// der deutsche Server-Text als Fallback fürs Log/für unbekannte Codes). Jeder
// bestehende Aufrufer, der `err.message || t('eigener.fallback')` macht, funktioniert
// dadurch unverändert weiter, zeigt aber jetzt den übersetzten statt immer den
// deutschen Text.
//
// FormData behält die vom Browser erzeugte Multipart-Boundary. JSON und private
// Downloads teilen denselben einmaligen Token-Refresh und dieselben Fehler.

import { t } from '../i18n/index.js';
// signedOut.js hängt an NICHTS — darum kein Ringschluss, obwohl auth.js diese
// Datei nutzt und diese Datei den Rauswurf-Grund schreibt.
import { rememberSignOut } from './signedOut.js';

const TOKEN_KEY = 'levelupwerk:token';

function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Liest die lokalisierte Fehlermeldung aus einem geparsten Response-Body.
 * Erwartet die Standardform { error: { code, message, details? } }; für Robustheit
 * (Netzwerkfehler, unerwartete Antworten) fällt sie sauber auf 'HTTP <status>' zurück.
 */
export function readErrorMessage(body, status) {
  const code = body?.error?.code;
  const serverMessage = body?.error?.message;
  if (code) {
    const details = body.error.details;
    const translated = t('errors.' + code, details && typeof details === 'object' && !Array.isArray(details) ? details : {});
    // t() gibt den Key selbst zurück, wenn er fehlt — daran erkennen wir einen
    // unbekannten Code und fallen auf den Server-Text zurück statt den rohen Key zu zeigen.
    if (translated !== 'errors.' + code) return translated;
  }
  return serverMessage || t('errors.GENERIC') || ('HTTP ' + status);
}

/**
 * Authentifizierter JSON-Request. Wirft bei !res.ok einen Error mit lokalisierter
 * .message, plus .status (HTTP-Statuscode) und .code (Server-Fehlercode, für Aufrufer,
 * die gezielt auf eine bestimmte Bedeutung reagieren müssen — z.B. 'CUSTOMER_HAS_
 * DOCUMENTS'). .serverMessage trägt den rohen deutschen Text fürs Debuggen/Log,
 * NIE für die UI verwenden.
 *
 * @param {string} path
 * @param {RequestInit & { auth?: boolean }} opts  auth: false unterdrückt den
 *   Authorization-Header (z.B. Login/Register, bevor ein Token existiert).
 */
// Single-Flight für den Token-Refresh: die Rotation macht den alten Refresh-Token
// sofort ungültig — würden zwei parallel abgelaufene Requests beide /api/auth/refresh
// aufrufen, käme der zweite mit dem schon rotierten Token an und die Session stürbe.
// Deshalb teilen sich alle gleichzeitigen 401er EINEN Refresh-Versuch.
// (auth.js wird dynamisch importiert, weil es selbst apiRequest importiert — Zyklus.)
let refreshInFlight = null;

function refreshOnce() {
  if (!refreshInFlight) {
    refreshInFlight = import('./auth.js')
      .then((auth) => auth.tryRefresh({ throwOnTransient: true }))
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

async function requestResponse(path, opts = {}) {
  const { auth = true, _retried = false, ...rest } = opts;
  const token = auth ? getToken() : null;
  const headers = new Headers(rest.headers);
  if (!headers.has('Content-Type') && !(rest.body instanceof FormData)) headers.set('Content-Type', 'application/json');
  if (token && !headers.has('Authorization')) headers.set('Authorization', 'Bearer ' + token);
  let res;
  try { res = await fetch(path, { ...rest, headers }); }
  catch (cause) {
    if (cause?.name === 'AbortError' || rest.signal?.aborted) throw cause;
    throw Object.assign(new Error(t('cohesion.networkFailure')), { code: 'NETWORK_FAILURE', cause });
  }

  if (!res.ok) {
    let body = {};
    try { body = await res.json(); } catch { /* Antwort ohne Body */ }
    // Abgelaufener Access-Token (12h, CASA 2.2.3): einmal still refreshen und den
    // Request wiederholen. Nur für authentifizierte Aufrufe — Login/Refresh/Logout
    // laufen mit auth:false und können hier nie in eine Schleife geraten.
    // AUSNAHME: wurde die Sitzung ENTZOGEN («ein Konto, ein Gerät»), ist ein
    // Refresh-Versuch sinnlos — der Server lehnt auch das Refresh-Token ab.
    // Schlimmer: der Versuch würde den Grund verschlucken und aus dem Rauswurf
    // ein gewöhnliches «Token abgelaufen» machen.
    const revoked = typeof body?.error?.code === 'string' && body.error.code.startsWith('AUTH_SESSION_REVOKED');
    if (res.status === 401 && auth && !_retried && !revoked) {
      if (await refreshOnce()) {
        return requestResponse(path, { ...opts, _retried: true });
      }
    }
    const err = new Error(readErrorMessage(body, res.status));
    err.status = res.status;
    err.code = body?.error?.code;
    err.serverMessage = body?.error?.message;
    err.details = body?.error?.details;
    const retryAfter = res.headers.get('Retry-After');
    if (retryAfter) {
      const seconds = Number(retryAfter);
      const delay = Number.isFinite(seconds) ? seconds * 1000 : Date.parse(retryAfter) - Date.now();
      if (Number.isFinite(delay)) err.retryAfterMs = Math.max(0, delay);
    }
    // Von einem anderen Gerät abgemeldet: den Grund merken (er muss einen
    // App-Neustart überleben) und die App zurück an die Anmeldemaske schicken.
    // Zentral hier und nicht in jedem Aufrufer — es kann JEDE Anfrage treffen.
    if (revoked) {
      rememberSignOut(err.code, err.details?.revokedByDevice ?? null);
      window.dispatchEvent(new CustomEvent('lw:session-revoked'));
    }
    // Plattform-Konten ohne zweiten Faktor sperrt der Server aus (requirePlatformMfa).
    // Die Einrichtung liegt sonst nur im Firmen-Dashboard, das diese Konten nie
    // sehen — ohne diesen Hinweis wäre die Fehlermeldung eine Sackgasse.
    if (err.code === 'MFA_SETUP_REQUIRED') {
      window.dispatchEvent(new CustomEvent('lw:mfa-setup-required'));
    }
    throw err;
  }
  return res;
}

export async function apiRequest(path, opts = {}) {
  const res = await requestResponse(path, opts);
  if (res.status === 204 || res.status === 205) return {};
  try { return await res.json(); }
  catch (cause) { throw Object.assign(new Error(t('cohesion.invalidResponse')), { code: 'INVALID_RESPONSE', cause }); }
}

/**
 * Authenticated multipart upload with real progress (fetch cannot report upload progress).
 * Same token, one silent refresh on 401 and the same localized errors as apiRequest.
 * @param {string} path
 * @param {FormData} body
 * @param {{ onProgress?: (loaded: number, total: number) => void, signal?: AbortSignal }} [opts]
 */
export function apiUpload(path, body, opts = {}, _retried = false) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', path);
    const token = getToken();
    if (token) xhr.setRequestHeader('Authorization', 'Bearer ' + token);
    xhr.responseType = 'text';
    xhr.upload.onprogress = (event) => { if (event.lengthComputable) opts.onProgress?.(event.loaded, event.total); };
    const abort = () => xhr.abort();
    opts.signal?.addEventListener('abort', abort, { once: true });
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
    xhr.onerror = () => reject(Object.assign(new Error(t('cohesion.networkFailure')), { code: 'NETWORK_FAILURE' }));
    xhr.onload = async () => {
      opts.signal?.removeEventListener('abort', abort);
      let parsed = {};
      try { parsed = xhr.responseText ? JSON.parse(xhr.responseText) : {}; } catch { parsed = {}; }
      if (xhr.status >= 200 && xhr.status < 300) return resolve(parsed);
      const revoked = typeof parsed?.error?.code === 'string' && parsed.error.code.startsWith('AUTH_SESSION_REVOKED');
      if (xhr.status === 401 && !_retried && !revoked) {
        try { if (await refreshOnce()) return resolve(await apiUpload(path, body, opts, true)); }
        catch (error) { return reject(error); }
      }
      const err = new Error(readErrorMessage(parsed, xhr.status));
      err.status = xhr.status; err.code = parsed?.error?.code; err.serverMessage = parsed?.error?.message; err.details = parsed?.error?.details;
      if (revoked) {
        rememberSignOut(err.code, err.details?.revokedByDevice ?? null);
        window.dispatchEvent(new CustomEvent('lw:session-revoked'));
      }
      reject(err);
    };
    xhr.send(body);
  });
}

/** Private file bytes never put bearer tokens in a URL or public cache. */
export async function apiBlob(path, opts = {}) {
  const res = await requestResponse(path, { ...opts, cache: 'no-store' });
  return res.blob();
}
